import React from 'react'

/**
 * Faithful CSS/SVG recreation of the IATA / 49 CFR Class 9 Dry Ice (UN1845)
 * shipping label, sized for a 4x6 thermal label.
 *
 * The artwork is fixed; only the dry-ice weight, the Shipper (FROM) block and
 * the Consignee (TO) block are data-driven. The whole thing is laid out at a
 * 4in x 6in footprint (LABEL_W x LABEL_H px at 96dpi) so html-to-image can
 * snapshot the #dry-ice-label node 1:1 and scale it up to 300dpi on export.
 */

// 4x6 inches @ 96 CSS px/in. Export scales this up for print DPI.
export const LABEL_W = 384 // 4in
export const LABEL_H = 576 // 6in

export interface LabelParty {
  name: string
  line1: string
  line2: string
}

export interface DryIceLabelData {
  dryIceKg: string // net weight per package (kg)
  numPackages: number // number of packages
  shipper: LabelParty // FROM — Shipper's Name and Address
  consignee: LabelParty // TO — Consignee Name and Address
  orderNumber?: string
}

const black = '#000'

const partyLines = (p: LabelParty) => (
  <div style={{ marginTop: 2, lineHeight: 1.25 }}>
    <div style={{ fontWeight: 700, fontSize: 9 }}>{p.name || ' '}</div>
    <div style={{ fontSize: 8.5 }}>{p.line1 || ' '}</div>
    <div style={{ fontSize: 8.5 }}>{p.line2 || ' '}</div>
  </div>
)

const DryIceLabel: React.FC<{ data: DryIceLabelData }> = ({ data }) => {
  return (
    <div
      id="dry-ice-label"
      style={{
        width: LABEL_W,
        height: LABEL_H,
        background: '#fff',
        color: black,
        boxSizing: 'border-box',
        fontFamily: 'Arial, Helvetica, sans-serif',
        position: 'relative',
        padding: 10,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Outer hazard-class border (heavy rule around whole label) */}
      <div
        style={{
          flex: 1,
          border: `2px solid ${black}`,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        {/* ---- Top instructional text, two columns ---- */}
        <div style={{ display: 'flex', borderBottom: `1.5px solid ${black}` }}>
          <div
            style={{
              flex: 1,
              padding: '6px 7px',
              borderRight: `1.5px solid ${black}`,
              fontSize: 7.5,
              lineHeight: 1.3,
            }}
          >
            <div>Shipper's Declaration not Required.</div>
            <div>Part B is required.</div>
            <div style={{ marginTop: 5 }}>Dry Ice amount must be in</div>
            <div>kilograms.</div>
            <div style={{ marginTop: 5 }}>Note: 2 lbs. = 1 kg.</div>
          </div>
          <div style={{ flex: 1, padding: '6px 7px', fontSize: 7.5, lineHeight: 1.3 }}>
            <div>Airwaybills/Airbills must have the following:</div>
            <div style={{ marginTop: 4, fontWeight: 700 }}>
              1. &ldquo;Dangerous Goods - Shipper's
            </div>
            <div style={{ fontWeight: 700, paddingLeft: 9 }}>Declaration not required&rdquo;</div>
            <div style={{ marginTop: 3, fontWeight: 700 }}>2. Dry Ice: 9; UN1845;</div>
            <div style={{ marginTop: 3, display: 'flex', alignItems: 'flex-end', gap: 2 }}>
              <span>3.</span>
              <span style={{ borderBottom: `1px solid ${black}`, minWidth: 34, textAlign: 'center' }}>
                {data.dryIceKg || ' '}
              </span>
              <span>x</span>
              <span style={{ borderBottom: `1px solid ${black}`, minWidth: 26, textAlign: 'center' }}>
                {data.numPackages || 1}
              </span>
              <span>=</span>
              <span style={{ borderBottom: `1px solid ${black}`, minWidth: 26, textAlign: 'center' }}>
                {(Number(data.dryIceKg) * (data.numPackages || 1)).toString()}
              </span>
              <span>kg</span>
            </div>
            <div style={{ display: 'flex', fontSize: 5.5, paddingLeft: 12, gap: 18, marginTop: 1 }}>
              <span>(number pkgs.)</span>
              <span>(net.)</span>
            </div>
          </div>
        </div>

        {/* ---- Center hazard diamond (Class 9) ---- */}
        <div
          style={{
            flex: 1,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 8,
          }}
        >
          <Class9Diamond />
        </div>

        {/* ---- Bottom band: DRY ICE / 9 / UN1845 ---- */}
        <div
          style={{
            borderTop: `1.5px solid ${black}`,
            display: 'flex',
            alignItems: 'stretch',
          }}
        >
          <div style={{ flex: 1, padding: '6px 8px' }}>
            <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: -0.5, lineHeight: 1 }}>
              DRY ICE
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, marginTop: 2 }}>
              <span
                style={{
                  borderBottom: `1.5px solid ${black}`,
                  minWidth: 52,
                  fontSize: 16,
                  fontWeight: 700,
                  textAlign: 'center',
                  lineHeight: 1.1,
                }}
              >
                {data.dryIceKg || ' '}
              </span>
              <span style={{ fontSize: 16, fontWeight: 800 }}>kg.</span>
            </div>
          </div>
          <div
            style={{
              width: 46,
              borderLeft: `1.5px solid ${black}`,
              borderRight: `1.5px solid ${black}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 30,
              fontWeight: 800,
            }}
          >
            9
          </div>
          <div
            style={{
              flex: 1,
              padding: '6px 8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: -0.5 }}>UN1845</div>
          </div>
        </div>

        {/* ---- Name & address footer ---- */}
        <div style={{ borderTop: `1.5px solid ${black}`, display: 'flex' }}>
          <div style={{ flex: 1, padding: '4px 8px 7px', borderRight: `1.5px solid ${black}` }}>
            <div style={{ fontSize: 7.5 }}>Shipper's Name and Address</div>
            {partyLines(data.shipper)}
          </div>
          <div style={{ flex: 1, padding: '4px 8px 7px' }}>
            <div style={{ fontSize: 7.5 }}>Consignee Name and Address</div>
            {partyLines(data.consignee)}
          </div>
        </div>
      </div>

      {data.orderNumber ? (
        <div style={{ fontSize: 7, textAlign: 'right', marginTop: 3, color: '#444' }}>
          {data.orderNumber}
        </div>
      ) : null}
    </div>
  )
}

/**
 * The black-and-white Class 9 hazard diamond: bottom half filled, top half
 * holds seven vertical bars (the "miscellaneous dangerous goods" mark).
 */
const Class9Diamond: React.FC = () => {
  const size = 240
  return (
    <svg width="100%" viewBox={`0 0 ${size} ${size}`} style={{ maxHeight: '100%' }}>
      {/* Diamond outline (rotated square) */}
      <rect
        x={size * 0.16}
        y={size * 0.16}
        width={size * 0.68}
        height={size * 0.68}
        transform={`rotate(45 ${size / 2} ${size / 2})`}
        fill="none"
        stroke={black}
        strokeWidth={3}
      />
      {/* Inner thin diamond line near the edge */}
      <rect
        x={size * 0.2}
        y={size * 0.2}
        width={size * 0.6}
        height={size * 0.6}
        transform={`rotate(45 ${size / 2} ${size / 2})`}
        fill="none"
        stroke={black}
        strokeWidth={1}
      />
      {/* Bottom solid black half of the diamond (clip to the diamond shape) */}
      <defs>
        <clipPath id="diamondClip">
          <rect
            x={size * 0.16}
            y={size * 0.16}
            width={size * 0.68}
            height={size * 0.68}
            transform={`rotate(45 ${size / 2} ${size / 2})`}
          />
        </clipPath>
      </defs>
      <g clipPath="url(#diamondClip)">
        {/* Lower triangle of the diamond, solid black */}
        <rect x={0} y={size / 2} width={size} height={size / 2} fill={black} />
        {/* Seven vertical bars: tops form an inverted-V peak, bottoms run down
            into (and merge with) the black lower half. */}
        {Array.from({ length: 7 }).map((_, i) => {
          const barW = size * 0.05
          const gap = size * 0.022
          const totalW = 7 * barW + 6 * gap
          const startX = size / 2 - totalW / 2
          const x = startX + i * (barW + gap)
          // Center bar tallest (reaches near the top vertex); outer bars shorter.
          const dist = Math.abs(i - 3)
          const topY = size * 0.17 + dist * size * 0.06
          return (
            <rect key={i} x={x} y={topY} width={barW} height={size - topY} fill={black} />
          )
        })}
      </g>
    </svg>
  )
}

export default DryIceLabel
